import React, { useMemo } from "react";

import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppLink from "../../design-system/components/AppLink";
import AppPersonBadge from "../../design-system/components/AppPersonBadge";
import AppText from "../../design-system/components/AppText";
import {
  IconArrowRight,
  IconChevronLeft,
  IconChevronRight,
  IconLocation,
} from "../../design-system/icons";
// Direkt aus MUI wie in der Sidebar: dort steht dasselbe Icon am Menuepunkt
// Genehmigungen, damit Karte und Navigation zusammengehoeren.
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import {
  DepartmentDonut,
  MonthlyHoursChart,
  PlanningStatusGrid,
  PlanningStatusLegend,
} from "./DashboardCharts";
import {
  buildCurrentDeployment,
  initials,
  monthLabel,
  shiftMonth,
  sortCurrentShifts,
} from "./dashboardModel";

function EmployeeAvatar({ name, color, neutral = false }) {
  return (
    <AppPersonBadge
      className={`admin-dashboard-avatar${neutral ? " admin-dashboard-avatar--neutral" : ""}`}
      name={name}
      initials={initials(name)}
      size="lg"
      tone={neutral ? "neutral" : "custom"}
      color={neutral ? undefined : color}
    />
  );
}

export function OpenRequestsRow({ count, onOpen, t }) {
  if (!(count > 0)) return null;
  const label = t("dashboard.openRequestsCount", {
    count,
    defaultValue: "{{count}} offene Genehmigungen",
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen?.();
    }
  };

  return (
    <AppCallout
      variant="warning"
      icon={<LibraryAddCheckIcon />}
      title={label}
      className="admin-dashboard-request-row admin-dashboard-request-row--interactive"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      aria-label={label}
    />
  );
}

export function DashboardLocationTitle({ locationName }) {
  if (!locationName) return null;
  return (
    <span className="admin-dashboard-location-context">
      <IconLocation aria-hidden="true" />
      <span>{locationName}</span>
    </span>
  );
}

function shiftDisplayName(shift) {
  return String(
    shift?.shift_name
      || shift?.shiftName
      || shift?.custom_shift_name
      || shift?.customShiftName
      || shift?.shift?.name
      || shift?.custom_shift?.name
      || ""
  ).trim();
}

export function CurrentShiftsCard({
  shifts = [],
  getDepartmentColor,
  showShiftplanLink,
  onOpenShiftplan,
  t,
}) {
  const visible = useMemo(() => sortCurrentShifts(shifts), [shifts]);
  const unassignedLabel = t("dashboard.noDepartment", "Ohne Abteilung");
  const deployment = useMemo(
    () => buildCurrentDeployment(shifts, {
      unassignedLabel,
      colorForDepartment: getDepartmentColor,
    }),
    [getDepartmentColor, shifts, unassignedLabel]
  );

  return (
    <AppCard className="admin-dashboard-card admin-dashboard-current-card">
      <AppCardHeader titleVariant="compact" className="admin-dashboard-card__header">
        <AppText as="h2" variant="heading-3" className="ds-card__title">
          {t("dashboard.currentlyWorking", "Jetzt im Einsatz")}
        </AppText>
      </AppCardHeader>
      <AppCardBody className="admin-dashboard-card__body admin-dashboard-current-card__body">
        <div className="admin-dashboard-current-card__split">
          <DepartmentDonut
            layout="stacked"
            items={deployment.items}
            total={deployment.total}
            getDepartmentColor={getDepartmentColor}
            showPercent={false}
            renderValue={(item) => item.count}
            totalLabel={t("dashboard.deployedNow", "im Einsatz")}
            centerValue={deployment.total}
            centerUnit={t("dashboard.deployedNow", "im Einsatz")}
            emptyLabel={t("dashboard.noDepartment", "Ohne Abteilung")}
          />
          <div className="admin-dashboard-current-card__people">
            {visible.length > 0 ? (
              <div className="admin-dashboard-current-list-wrap">
                <ul className="admin-dashboard-current-list">
                  {visible.map((shift, index) => {
                    const color = shift?.department_color
                      || shift?.color
                      || getDepartmentColor(shift?.department);
                    const end = shift?.end || shift?.shifts?.[0]?.end || "--:--";
                    const department = shift?.department
                      || t("dashboard.noDepartment", "Ohne Abteilung");
                    const shiftName = shiftDisplayName(shift);
                    return (
                      <li
                        className="admin-dashboard-current-list__item"
                        key={`${shift?.id || shift?.user_id || shift?.name || "shift"}-${index}`}
                      >
                        <EmployeeAvatar name={shift?.name} color={color} neutral />
                        <div className="admin-dashboard-current-list__person">
                          <AppText as="strong" variant="label-2" truncate>
                            {shift?.name || "–"}
                          </AppText>
                          <AppText variant="body-3" tone="secondary" truncate>
                            <span
                              className="admin-dashboard-current-list__department"
                              style={{ color }}
                            >
                              {department}
                            </span>
                            {shiftName ? <> · {shiftName}</> : null}
                            {" · "}{t("dashboard.until", "bis")} {end}
                          </AppText>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="admin-dashboard-empty-state">
                <AppText variant="body-2" tone="secondary">
                  {t(
                    "dashboard.noOneScheduled",
                    "Laut Schichtplan ist gerade niemand eingeplant."
                  )}
                </AppText>
              </div>
            )}
          </div>
        </div>
      </AppCardBody>
      {showShiftplanLink ? (
        <div className="admin-dashboard-card__action">
          <AppLink size="sm" onClick={onOpenShiftplan} trailingIcon={<IconArrowRight />}>
            {t("dashboard.toShiftplan", "Zum Schichtplan")}
          </AppLink>
        </div>
      ) : null}
    </AppCard>
  );
}

export function PlanningStatusCard({
  planningStatus,
  getDepartmentColor,
  showDemandLink,
  onOpenDemand,
  locale,
  t,
}) {
  const openShiftCount = planningStatus?.openShiftCount || 0;
  const hasDepartments = (planningStatus?.departments || []).length > 0;

  return (
    <AppCard className="admin-dashboard-card admin-dashboard-staffing-card">
      <AppCardHeader
        titleVariant="compact"
        className="admin-dashboard-card__header admin-dashboard-staffing-card__header"
      >
        <div className="ds-card__header-main">
          <AppText as="h2" variant="heading-3" className="ds-card__title">
            {t("dashboard.planningStatus", "Planungsstatus nächste 7 Tage")}
          </AppText>
          <AppText as="p" className="ds-card__subtitle">
            {t("dashboard.planningStatusSubtitle", "Bedarf und geplante Schichten")}
          </AppText>
        </div>
        {/* Kennzahlen stehen rechts im Kopf, damit der Rumpf allein der
            Tabelle gehoert und die Karte flacher bleibt. */}
        <div className="admin-dashboard-planning-kpis">
          <p className={`admin-dashboard-planning-kpi${openShiftCount > 0 ? " is-open" : ""}`}>
            <strong>{openShiftCount}</strong>
            <span>
              {t("dashboard.openShiftsUnit", {
                count: openShiftCount,
                defaultValue: "offene Schichten",
              })}
            </span>
          </p>
        </div>
      </AppCardHeader>
      <AppCardBody inset="none" className="admin-dashboard-staffing-card__body">
        <PlanningStatusGrid
          planningStatus={planningStatus}
          getDepartmentColor={getDepartmentColor}
          locale={locale}
          labels={{
            gridLabel: t("dashboard.planningStatusLabel", "Deckung je Abteilung und Tag"),
            empty: t(
              "dashboard.noPlanningStatus",
              "Für die nächsten 7 Tage sind weder Bedarf noch Schichten hinterlegt."
            ),
            coveredOf: ({ covered, required }) => t("dashboard.coveredOf", {
              covered,
              required,
              defaultValue: "{{covered}} von {{required}} gedeckt",
            }),
            noDemand: ({ planned }) => t("dashboard.noDemandPlanned", {
              count: planned,
              defaultValue: "Kein Bedarf hinterlegt · {{count}} Einsätze",
            }),
            openShifts: ({ count }) => t("dashboard.openShiftsShort", {
              count,
              defaultValue: "{{count}} Schicht offen",
            }),
          }}
        />
      </AppCardBody>
      {showDemandLink || hasDepartments ? (
        <div className="admin-dashboard-card__action admin-dashboard-staffing-card__action">
          {showDemandLink ? (
            <AppLink size="sm" onClick={onOpenDemand} trailingIcon={<IconArrowRight />}>
              {t("dashboard.toDemand", "Zur Bedarfsplanung")}
            </AppLink>
          ) : <span />}
          {hasDepartments ? (
            <PlanningStatusLegend
              coveredLabel={t("dashboard.covered", "Gedeckt")}
              openLabel={t("dashboard.open", "Offen")}
            />
          ) : null}
        </div>
      ) : null}
    </AppCard>
  );
}

export function MonthlyOverviewCard({
  data,
  dailyData,
  total,
  breakdownTotalHours = null,
  changePercent,
  selectedMonth,
  loading,
  onMonthChange,
  getDepartmentColor,
  formatHours,
  locale,
  t,
}) {
  const previousLabel = new Intl.DateTimeFormat(locale, { month: "long" }).format(
    new Date(Number(selectedMonth.slice(0, 4)), Number(selectedMonth.slice(5, 7)) - 2, 1, 12)
  );
  const hasComparison = changePercent != null;
  const sign = changePercent > 0 ? "+" : "";
  const formattedChange = hasComparison
    ? new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(changePercent)
    : "";
  // Ferien und Absenzen kommen als eigene Eintraege aus dem Backend; die
  // Donut-Mitte zeigt deshalb die Gesamtsumme ueber alle drei Stundenarten.
  const breakdownTotal = breakdownTotalHours ?? total;
  const centerValue = formatHours(breakdownTotal, false);
  const centerUnit = t("dashboard.hoursShort", "Std");

  const emptyMonthLabel = t(
    "dashboard.noReleasedShiftsMonth",
    "In diesem Monat gibt es keine Schichten."
  );

  // Beide Monatsauswertungen teilen sich eine Karte und den Monatswechsler;
  // getrennt werden sie nur durch den Strich zwischen den Haelften.
  return (
    <AppCard
      className="admin-dashboard-card admin-dashboard-month-card"
      aria-busy={loading}
    >
      <div className="admin-dashboard-month-card__panels">
        <section className="admin-dashboard-month-card__panel">
          <div className="admin-dashboard-month-card__panel-head">
            <AppText as="h2" variant="heading-4" className="ds-card__title">
              {/*
                Der Monatswechsler steht in der rechten Haelfte, gilt aber fuer
                beide — deshalb traegt diese Haelfte den Monat im Titel. Er
                steht bewusst leichter als der Titel selbst: eine Einordnung,
                keine zweite Ueberschrift.
              */}
              {t("dashboard.monthlyHoursByDepartment", "Einsatzstunden nach Bereich")}{" "}
              <span className="admin-dashboard-month-card__panel-month">
                ({monthLabel(selectedMonth, locale)})
              </span>
            </AppText>
          </div>
          <div className="admin-dashboard-month-card__breakdown">
            <DepartmentDonut
              items={data}
              total={breakdownTotal}
              getDepartmentColor={getDepartmentColor}
              formatHours={formatHours}
              totalLabel={t("dashboard.total", "Gesamt")}
              centerValue={centerValue}
              centerUnit={centerUnit}
              emptyLabel={emptyMonthLabel}
            />
          </div>
          <div className="admin-dashboard-month-card__comparison">
            {hasComparison ? (
              <AppText
                variant="body-3"
                tone="secondary"
                className="admin-dashboard-hours-card__comparison"
              >
                <strong className={changePercent < 0 ? "is-negative" : ""}>
                  {sign}{formattedChange}%
                </strong>
                {" "}{t("dashboard.comparedWith", {
                  month: previousLabel,
                  defaultValue: "vs. {{month}}",
                })}
              </AppText>
            ) : (
              <AppText variant="body-3" tone="secondary">
                {t("dashboard.noComparisonData", "Keine Vergleichsdaten")}
              </AppText>
            )}
          </div>
        </section>

        <section className="admin-dashboard-month-card__panel admin-dashboard-month-card__panel--daily">
          <div className="admin-dashboard-month-card__panel-head">
            <AppText as="h2" variant="heading-4" className="ds-card__title">
              {t("dashboard.hoursPerDay", "Stunden pro Tag")}
            </AppText>
            <div
              className="admin-dashboard-month-nav"
              role="group"
              aria-label={t("dashboard.monthNavigation", "Monatsnavigation")}
            >
              <button
                type="button"
                aria-label={t("dashboard.previousMonth", "Vorheriger Monat")}
                onClick={() => onMonthChange(shiftMonth(selectedMonth, -1))}
                disabled={loading}
              >
                <IconChevronLeft aria-hidden="true" />
              </button>
              <span>{monthLabel(selectedMonth, locale)}</span>
              <button
                type="button"
                aria-label={t("dashboard.nextMonth", "Nächster Monat")}
                onClick={() => onMonthChange(shiftMonth(selectedMonth, 1))}
                disabled={loading}
              >
                <IconChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="admin-dashboard-month-card__chart">
            <MonthlyHoursChart
              data={dailyData}
              formatHours={formatHours}
              locale={locale}
              emptyLabel={emptyMonthLabel}
              ariaLabel={t("dashboard.monthly_hours_overview", "Monatlicher Stunden-Überblick")}
              seriesLabels={{
                shift: t("dashboard.shiftHours", "Schichtstunden"),
                holiday: t("dashboard.holidayHours", "Ferienstunden"),
                absence: t("dashboard.absenceHours", "Absenzstunden"),
              }}
            />
          </div>
        </section>
      </div>
    </AppCard>
  );
}
